import { useStore } from 'vuex'

import { localId } from '@/helpers/nestedAttributes'

// Copy-on-write edits to the JSON:API records in the Vuex cache.
//
// A page that renders straight from the cache, and used to autosave, can keep
// rendering unsaved edits the same way: write each edit into the cache as a
// patched copy, and keep the record as it was loaded so Discard can put it
// back. A record the merchant creates before saving lives in the cache under a
// local id until the save's response brings the real one, at which point the
// stand-in is dropped.
//
// Nothing here talks to the server; that is the caller's job.
export function useStagedRecords(store = useStore()) {
  // As loaded, keyed by type:id, remembered the first time a record is touched.
  const originals = new Map()
  // Records that exist only on this page, keyed the same way.
  const temps = new Map()

  const keyOf = (type, id) => `${type}:${id}`

  const read = (type, id) => store.state.objects?.[type]?.[id]

  const commit = record => store.commit('RECEIVE_OBJECT', { data: record })

  const destroy = (type, id) => {
    if (read(type, id)) store.commit('DESTROY_OBJECT', { data: { type, id } })
  }

  const isTemp = (type, id) => temps.has(keyOf(type, id))

  // The record as it was loaded, whether or not it has been touched since.
  const original = (type, id) => originals.get(keyOf(type, id)) || read(type, id)

  const remember = record => {
    const key = keyOf(record.type, record.id)
    if (!originals.has(key) && !temps.has(key)) originals.set(key, record)
  }

  // Replaces the cached record with a copy carrying the given attributes and
  // relationships on top of its own. Returns the copy, or null if the record is
  // not in the cache.
  const patchRecord = (type, id, { attributes = {}, relationships = {} } = {}) => {
    const record = read(type, id)
    if (!record) return null
    remember(record)
    const next = {
      ...record,
      attributes: { ...record.attributes, ...attributes },
      relationships: { ...record.relationships, ...relationships }
    }
    commit(next)
    return next
  }

  // Puts a record that exists only on this page into the cache.
  const addRecord = ({ type, id = localId(), attributes = {}, relationships = {} }) => {
    const record = { type, id, attributes, relationships }
    temps.set(keyOf(type, id), { type, id })
    commit(record)
    return record
  }

  // Drops a record that exists only on this page: the merchant took it back.
  const forgetRecord = (type, id) => {
    temps.delete(keyOf(type, id))
    destroy(type, id)
  }

  const ref = record => ({ type: record.type, id: record.id })

  const relationshipData = (type, id, name) => read(type, id)?.relationships?.[name]?.data

  // Points a has_one relationship at a record (or at nothing).
  const linkOne = (type, id, name, record) =>
    patchRecord(type, id, { relationships: { [name]: { data: record ? ref(record) : null } } })

  // Adds a record to a has_many relationship.
  const linkMany = (type, id, name, record) => {
    const data = relationshipData(type, id, name)
    const list = Array.isArray(data) ? data : []
    if (list.some(x => x.type === record.type && String(x.id) === String(record.id))) return read(type, id)
    return patchRecord(type, id, { relationships: { [name]: { data: [...list, ref(record)] } } })
  }

  // Takes a record out of a has_many relationship.
  const unlinkMany = (type, id, name, record) => {
    const data = relationshipData(type, id, name)
    if (!Array.isArray(data)) return read(type, id)
    const list = data.filter(x => !(x.type === record.type && String(x.id) === String(record.id)))
    return patchRecord(type, id, { relationships: { [name]: { data: list } } })
  }

  // Puts one record back as it was loaded.
  const restoreRecord = (type, id) => {
    const key = keyOf(type, id)
    const original = originals.get(key)
    if (!original) return
    commit(original)
    originals.delete(key)
  }

  // Discard: every touched record back as loaded, every stand-in gone.
  const restoreAll = () => {
    for (const original of originals.values()) commit(original)
    for (const { type, id } of temps.values()) destroy(type, id)
    originals.clear()
    temps.clear()
  }

  // After a save: the server's response is the truth now, so the stand-ins go
  // and there is nothing left to put back.
  const settle = () => {
    for (const { type, id } of temps.values()) destroy(type, id)
    originals.clear()
    temps.clear()
  }

  return {
    read,
    original,
    isTemp,
    patchRecord,
    addRecord,
    forgetRecord,
    linkOne,
    linkMany,
    unlinkMany,
    restoreRecord,
    restoreAll,
    settle
  }
}
